import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";

import { WModalButton } from "@/components/button/WToolButton.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { cloneDiskMutation, type DiskWithPrice } from "@/modules/disks/disksListQuery.ts";
import { cn } from "@/utils/classNames.ts";
import { useGenerateUuid } from "@/utils/useGenerateUuid.ts";
import { useLingui } from "@lingui/react/macro";
import { Input } from "react-aria-components";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

interface DiskCloneInputs {
    name: string;
}

function DiskCloneModalContent({ disk }: { disk: DiskWithPrice }) {
    const { display_name: originalVmName, uuid, location, billing_account_id } = disk;

    //#region Hooks
    const { t } = useLingui();
    const cloneMutation = useStandardMutation(cloneDiskMutation);
    const requestUuid = useGenerateUuid(undefined);

    const initialName = originalVmName ? `${originalVmName}-clone` : "";
    //#endregion

    const onSubmit: SubmitHandler<DiskCloneInputs> = async (inputs) => {
        const { name } = inputs;

        await cloneMutation.mutateAsync({
            requestUuid,
            location,
            body: {
                billing_account_id,
                source_image_type: "DISK",
                source_image: uuid,
                display_name: name,
            },
        });
    };

    const form = useForm<DiskCloneInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            name: initialName,
        },
    });

    const { control } = form;

    return (
        <WModalForm form={form} onSubmit={onSubmit} title={t`Clone resource`} label={t`Clone`}>
            <Controller
                control={control}
                name="name"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        isRequired
                        autoFocus
                        headless
                        block
                        label={t`Resource name`}
                    >
                        <Input className={cn(FF.FormFieldInput, TF.Input)} />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}

export function DiskCloneModal({ disk }: { disk: DiskWithPrice }) {
    const { t } = useLingui();
    return (
        <WModal button={<WModalButton label={t`Clone`} icon="jp-duplicate-icon" />}>
            <DiskCloneModalContent disk={disk} />
        </WModal>
    );
}
